/**
 * Update Quality Dialog Component
 * Dialog for updating batch quality grade with validation and warnings
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-003
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useQualityUpdate } from '@/hooks/inventory/useQualityUpdate';
import type { BatchResponse, QualityGrade } from '@/types/inventory';
import { QualityGradeConfig, isPurityValidForGrade } from '@/types/inventory';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Calendar as CalendarIcon,
  TrendingDown,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// FORM VALIDATION SCHEMA
// ============================================

const qualityUpdateSchema = z.object({
  qualityGrade: z.enum(['A', 'B', 'C'], {
    required_error: 'Please select a quality grade',
  }),
  purityPercentage: z
    .number({
      required_error: 'Purity percentage is required',
      invalid_type_error: 'Purity must be a number',
    })
    .min(0, 'Purity cannot be less than 0%')
    .max(100, 'Purity cannot exceed 100%'),
  testDate: z.date({
    required_error: 'Test date is required',
  }).refine((date) => date <= new Date(), {
    message: 'Test date cannot be in the future',
  }),
  remarks: z
    .string()
    .min(10, 'Remarks must be at least 10 characters')
    .max(1000, 'Remarks cannot exceed 1000 characters'),
}).refine(
  (data) => {
    // Skip validation if purity or grade is undefined
    if (typeof data.purityPercentage !== 'number' || !data.qualityGrade) {
      return true;
    }
    return isPurityValidForGrade(data.purityPercentage, data.qualityGrade);
  },
  (data) => {
    try {
      const defaultGradeConfig = { minPurity: 0, maxPurity: 100 };
      const gradeConfig = (data.qualityGrade && QualityGradeConfig[data.qualityGrade])
        ? QualityGradeConfig[data.qualityGrade]
        : defaultGradeConfig;
      const safeConfig = gradeConfig || defaultGradeConfig;
      const minPurity = safeConfig?.minPurity ?? 0;
      const maxPurity = safeConfig?.maxPurity ?? 100;
      const purityDisplay = typeof data.purityPercentage === 'number' ? data.purityPercentage.toFixed(1) : 'unknown';
      return {
        message: `Purity ${purityDisplay}% does not match Grade ${data.qualityGrade} range (${minPurity}% - ${maxPurity}%)`,
        path: ['purityPercentage'],
      };
    } catch (err) {
      return {
        message: 'Purity does not match the selected quality grade range',
        path: ['purityPercentage'],
      };
    }
  }
);

type QualityUpdateFormValues = z.infer<typeof qualityUpdateSchema>;

// ============================================
// COMPONENT PROPS
// ============================================

interface UpdateQualityDialogProps {
  batch: BatchResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (batch: BatchResponse) => void;
}

// ============================================
// COMPONENT
// ============================================

export function UpdateQualityDialog({
  batch,
  open,
  onOpenChange,
  onSuccess,
}: UpdateQualityDialogProps) {
  const [selectedGrade, setSelectedGrade] = useState<QualityGrade>(batch.qualityGrade);
  const [purityValue, setPurityValue] = useState<number>(batch.purityPercentage);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { isUpdating, error, updateQuality, reset, isDowngrade } = useQualityUpdate({
    onSuccess: (updatedBatch) => {
      toast.success('Quality Updated', {
        description: `Batch ${batch.batchNumber} quality updated to Grade ${updatedBatch.qualityGrade}`,
      });
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(updatedBatch);
      }
    },
    onError: (error) => {
      toast.error('Update Failed', {
        description: error.message,
      });
    },
  });

  const form = useForm<QualityUpdateFormValues>({
    resolver: zodResolver(qualityUpdateSchema),
    defaultValues: {
      qualityGrade: batch.qualityGrade,
      purityPercentage: batch.purityPercentage,
      testDate: new Date(),
      remarks: '',
    },
  });

  // Reset form when dialog opens/closes or batch changes
  useEffect(() => {
    if (open) {
      form.reset({
        qualityGrade: batch.qualityGrade,
        purityPercentage: batch.purityPercentage,
        testDate: new Date(),
        remarks: '',
      });
      setSelectedGrade(batch.qualityGrade);
      setPurityValue(batch.purityPercentage);
      reset();
    }
  }, [open, batch.id, batch.qualityGrade, batch.purityPercentage]);

  // Update purity when grade changes
  useEffect(() => {
    const defaultConfig = {
      minPurity: 0,
      maxPurity: 100,
      label: 'Unknown Grade',
      description: 'Quality grade information not available',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-300'
    };
    const gradeConfig = (selectedGrade && QualityGradeConfig[selectedGrade]) ? QualityGradeConfig[selectedGrade] : defaultConfig;
    const currentPurity = form.getValues('purityPercentage');

    // If current purity doesn't match new grade range, suggest mid-range value
    if (!isPurityValidForGrade(currentPurity, selectedGrade)) {
      const minPurity = gradeConfig?.minPurity ?? 0;
      const maxPurity = gradeConfig?.maxPurity ?? 100;
      const suggestedPurity = (minPurity + maxPurity) / 2;
      setPurityValue(suggestedPurity);
      form.setValue('purityPercentage', suggestedPurity, { shouldValidate: true });
    }
  }, [selectedGrade]);

  // Handle form submission
  async function onSubmit(values: QualityUpdateFormValues) {
    const requestData = {
      qualityGrade: values.qualityGrade,
      purityPercentage: values.purityPercentage,
      testDate: format(values.testDate, 'yyyy-MM-dd'),
      remarks: values.remarks,
    };

    await updateQuality(batch.id, requestData);
  }

  // Check if user has proper role (simplified - should check actual user role)
  const hasPermission = true; // TODO: Implement actual role check

  // Check if downgrading
  const isDowngrading = isDowngrade(batch.qualityGrade, selectedGrade);

  // Check if batch has allocations
  const hasAllocations = batch.allocatedQuantity > 0;

  // Prevent downgrade if batch is allocated
  const cannotDowngrade = isDowngrading && hasAllocations;

  const defaultGradeConfig = {
    label: 'Unknown Grade',
    description: 'Quality grade information not available',
    minPurity: 0,
    maxPurity: 100,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300'
  };

  const gradeConfig = (selectedGrade && QualityGradeConfig[selectedGrade]) ? QualityGradeConfig[selectedGrade] : defaultGradeConfig;

  // Safe config for current batch quality
  const currentQualityConfig = (batch.qualityGrade && QualityGradeConfig[batch.qualityGrade]) ? QualityGradeConfig[batch.qualityGrade] : defaultGradeConfig;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Update Quality Grade
          </DialogTitle>
          <DialogDescription>
            Update quality grade for batch {batch.batchNumber}
          </DialogDescription>
        </DialogHeader>

        {/* Permission Warning */}
        {!hasPermission && (
          <Alert className="border-red-300 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              You do not have permission to update batch quality. Only Quality Inspectors and Admins can perform this action.
            </AlertDescription>
          </Alert>
        )}

        {/* Downgrade Warning */}
        {cannotDowngrade && (
          <Alert className="border-orange-300 bg-orange-50">
            <TrendingDown className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>Warning:</strong> Cannot downgrade batch quality after allocation.
              This batch has {batch.allocatedQuantity} {batch.quantityUnit} allocated.
              Please contact an administrator if quality downgrade is necessary.
            </AlertDescription>
          </Alert>
        )}

        {/* Downgrade Info (without allocation) */}
        {isDowngrading && !hasAllocations && (
          <Alert className="border-yellow-300 bg-yellow-50">
            <Info className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              You are downgrading from Grade {batch.qualityGrade} to Grade {selectedGrade}.
              Please provide detailed remarks explaining the reason.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Current Quality */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm text-gray-600">Current Quality</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      className={cn(
                        'text-base',
                        currentQualityConfig?.bgColor || 'bg-gray-50',
                        currentQualityConfig?.color || 'text-gray-600',
                        'border-none'
                      )}
                    >
                      Grade {batch.qualityGrade}
                    </Badge>
                    <span className="text-sm text-gray-600">
                      {(batch.purityPercentage ?? 0).toFixed(2)}% purity
                    </span>
                  </div>
                </div>
                {hasAllocations && (
                  <div className="text-right">
                    <Label className="text-sm text-gray-600">Allocated</Label>
                    <div className="text-sm font-semibold text-orange-600 mt-1">
                      {batch.allocatedQuantity} {batch.quantityUnit}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quality Grade Selection */}
            <FormField
              control={form.control}
              name="qualityGrade"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Quality Grade *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSelectedGrade(value as QualityGrade);
                      }}
                      value={field.value}
                      className="space-y-3"
                      disabled={!hasPermission || isUpdating}
                    >
                      {(['A', 'B', 'C'] as QualityGrade[]).map((grade) => {
                        const config = QualityGradeConfig[grade] || defaultGradeConfig;
                        return (
                          <div
                            key={grade}
                            className={cn(
                              'flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors',
                              field.value === grade
                                ? `${config?.borderColor || 'border-gray-300'} ${config?.bgColor || 'bg-gray-50'}`
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                          >
                            <RadioGroupItem value={grade} id={`grade-${grade}`} />
                            <div className="flex-1">
                              <Label
                                htmlFor={`grade-${grade}`}
                                className="font-semibold cursor-pointer flex items-center gap-2"
                              >
                                Grade {grade}
                                {field.value === grade && (
                                  <CheckCircle2 className={cn('w-4 h-4', config?.color || 'text-gray-600')} />
                                )}
                              </Label>
                              <p className="text-sm text-gray-600 mt-1">
                                {config?.description || 'Quality grade information not available'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Purity Range: {(config?.minPurity ?? 0).toFixed(1)}% - {(config?.maxPurity ?? 100).toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Purity Percentage */}
            <FormField
              control={form.control}
              name="purityPercentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purity Percentage *</FormLabel>
                  <FormDescription>
                    Grade {selectedGrade} range: {(gradeConfig?.minPurity ?? 0).toFixed(1)}% - {(gradeConfig?.maxPurity ?? 100).toFixed(1)}%
                  </FormDescription>
                  <FormControl>
                    <div className="space-y-4">
                      <Slider
                        min={0}
                        max={100}
                        step={0.1}
                        value={[purityValue]}
                        onValueChange={(values) => {
                          const newValue = values[0];
                          setPurityValue(newValue);
                          field.onChange(newValue);
                        }}
                        disabled={!hasPermission || isUpdating}
                        className="w-full"
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={purityValue}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setPurityValue(newValue);
                            field.onChange(newValue);
                          }}
                          disabled={!hasPermission || isUpdating}
                          className="w-24"
                        />
                        <span className="text-sm text-gray-600">%</span>
                        <div
                          className={cn(
                            'ml-auto text-sm font-semibold px-3 py-1 rounded',
                            isPurityValidForGrade(purityValue, selectedGrade)
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          )}
                        >
                          {isPurityValidForGrade(purityValue, selectedGrade)
                            ? 'Valid for Grade'
                            : 'Outside Grade Range'}
                        </div>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Test Date */}
            <FormField
              control={form.control}
              name="testDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Test Date *</FormLabel>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                          disabled={!hasPermission || isUpdating}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          setCalendarOpen(false);
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Date when quality test was performed (cannot be future)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remarks */}
            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter quality test results, observations, and any relevant notes..."
                      className="min-h-[100px] resize-none"
                      disabled={!hasPermission || isUpdating}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {field.value.length}/1000 characters (minimum 10 required)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error Display */}
            {error && (
              <Alert className="border-red-300 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!hasPermission || isUpdating || cannotDowngrade}
              >
                {isUpdating ? 'Updating...' : 'Update Quality'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
